import styled, { css } from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import React, { HTMLAttributes, useRef, useState } from 'react'
import { useOnClickOutside } from '@/helpers/useClickOutside'
import Box from '@components/Box'
import { IButton, SButtonHeader } from '@components/Button/Button'
import { STypographyDefault, STypographyParagraph } from '@components/Typography'
import { above } from '@styles/mixins/breakpoints'
import { md } from '@styles/variables/breakpoints'

export interface ICard extends HTMLAttributes<HTMLDivElement> {
  icon: React.ReactNode
  heading: string
  price?: string
  more?: React.ReactNode
  button?: IButton
}

interface IPropsFlip {
  flip?: boolean
}

const SCard = styled(Box)`
  box-sizing: border-box;
  width: 228px;
  height: 210px;

  ${above('md')(css`
    width: 303px;
    height: 172px;
  `)}
`

const SCardInner = styled.div<IPropsFlip>`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
`

const SCardFront = styled.div<IPropsFlip & HTMLAttributes<HTMLDivElement>>`
  padding: 24px;
  position: absolute;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden; /* Safari */
  backface-visibility: hidden;
  box-shadow: ${({ theme }) => theme.general.boxShadow};
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.blue};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  z-index: ${({ flip }) => (flip ? 0 : 1)};
  opacity: 1;
  transition: 0.5s;

  &.flip {
    transition: 0.5s;
    opacity: 0;
  }
`

const SCardBack = styled(SCardFront)`
  background: ${({ theme }) => theme.colors.blue};
  opacity: 1;
  z-index: 0;
`

const SIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 12px;
  flex-shrink: 0;
  flex-grow: 0;
  margin-left: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SMoreButton = styled(SButtonHeader)`
  flex-shrink: 0;
  flex-grow: 0;
  margin-top: 16px;

  ${above('md')(css`
    margin-left: 5px;
    margin-top: 0;
  `)}
`

const Card: React.FC<ICard> = ({ children, icon, heading, button, price, more, ...divProps }) => {
  const [flip, setFlip] = useState<boolean>(false)
  const isSm = useMediaQuery({ maxWidth: md - 1 })

  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside<HTMLDivElement>(ref, () => setFlip(false))

  return (
    <SCard {...divProps}>
      <SCardInner flip={flip}>
        <SCardFront className={flip ? 'flip' : ''} flip={flip}>
          <Box display="flex" justifyContent="space-between">
            <STypographyParagraph fontWeight="600">{heading}</STypographyParagraph>
            <SIcon>{icon}</SIcon>
          </Box>
          <Box display={{ _: 'block', md: 'flex' }} justifyContent="space-between" alignItems="flex-end">
            {price ? (
              <Box
                display="flex"
                flexDirection={{ _: 'column' }}
                width="50%"
                justifyContent="space-between"
                mt={{ _: '12px' }}
              >
                <STypographyDefault>{children}</STypographyDefault>
                <STypographyDefault fontWeight="600" mt={'4px'}>
                  {price} ₽/мес
                </STypographyDefault>
              </Box>
            ) : (
              <STypographyDefault mt={{ _: '4px', md: '8px' }}>{children}</STypographyDefault>
            )}
            {more ? (
              <SMoreButton type="black" onClick={() => setFlip(true)}>
                Подробнее
              </SMoreButton>
            ) : button && (
              <SMoreButton type="black" {...button} />
            )}
          </Box>
        </SCardFront>
        <SCardBack ref={ref} onClick={() => setFlip(false)}>
          {more}
        </SCardBack>
      </SCardInner>
    </SCard>
  )
}

export default Card
